import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";

/**
 * Validators that mirror backend FluentValidation rules exactly, so forms never send a request the
 * API would reject with HTTP 400. Error keys reuse the ones FormErrorComponent already renders.
 */
export class SecurityValidators {
  private static isEmpty(value: any): boolean {
    return value === undefined || value === null || `${value}`.length === 0;
  }

  /**
   * Backend Password() rule (Application/Extensions/RuleBuilderExtensions.cs): at least 8 characters,
   * an upper case letter, a lower case letter, a digit and a non-alphanumeric character.
   */
  static backendPassword(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as string;
      if (SecurityValidators.isEmpty(value)) {
        return null;
      }
      const valid =
        value.length >= 8 &&
        /[A-Z]/.test(value) &&
        /[a-z]/.test(value) &&
        /[0-9]/.test(value) &&
        /[^a-zA-Z0-9]/.test(value);
      return valid ? null : { password: true };
    };
  }

  /** Backend Phone() rule: "+" followed by 10 to 15 digits. */
  static internationalPhone(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as string;
      if (SecurityValidators.isEmpty(value)) {
        return null;
      }
      return /^\+[0-9]{10,15}$/.test(`${value}`.trim()) ? null : { phone: true };
    };
  }

  /** Re-validates `control` whenever `other` changes (e.g. confirm password). */
  static revalidateOnChange(other: AbstractControl, control: AbstractControl) {
    return other.valueChanges.subscribe(() =>
      control.updateValueAndValidity({ emitEvent: false }),
    );
  }

  /** IPv4 / IPv6 address or CIDR block (prefix 0-32 for IPv4, 0-128 for IPv6). */
  static ipOrCidr(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as string;
      if (SecurityValidators.isEmpty(value)) {
        return null;
      }
      const block = SecurityValidators.parseCidr(`${value}`.trim());
      if (!block) {
        return { ipAddress: true };
      }
      // Backend IPNetwork.TryParse rejects a CIDR whose host bits are set (41.66.1.2/16).
      return SecurityValidators.hasHostBits(block.bytes, block.prefix)
        ? { hostBits: true }
        : null;
    };
  }

  /** True when any bit after the prefix is non-zero. */
  static hasHostBits(bytes: number[], prefix: number): boolean {
    let bits = prefix;
    for (const b of bytes) {
      const networkMask = bits >= 8 ? 0xff : bits <= 0 ? 0 : (0xff << (8 - bits)) & 0xff;
      if ((b & ~networkMask & 0xff) !== 0) {
        return true;
      }
      bits -= 8;
    }
    return false;
  }

  /** IPv4-mapped IPv6 (::ffff:a.b.c.d[/p]) becomes IPv4 (prefix - 96), as the backend does. */
  private static unmapIpv4(bytes: number[], prefix: number): { bytes: number[]; prefix: number } {
    const mapped =
      bytes.length === 16 &&
      bytes.slice(0, 10).every((b) => b === 0) &&
      bytes[10] === 0xff &&
      bytes[11] === 0xff;
    return mapped ? { bytes: bytes.slice(12), prefix: Math.max(prefix - 96, 0) } : { bytes, prefix };
  }

  /** Returns the address bytes and prefix length, or null when the text is not a valid IP/CIDR. */
  static parseCidr(text: string): { bytes: number[]; prefix: number } | null {
    if (!text) {
      return null;
    }
    const parts = text.split("/");
    if (parts.length > 2) {
      return null;
    }
    const bytes = SecurityValidators.parseIp(parts[0]);
    if (!bytes) {
      return null;
    }
    const maxPrefix = bytes.length * 8;
    if (parts.length === 1) {
      return { bytes, prefix: maxPrefix };
    }
    if (!/^\d{1,3}$/.test(parts[1])) {
      return null;
    }
    const prefix = Number(parts[1]);
    if (prefix < 0 || prefix > maxPrefix) {
      return null;
    }
    return { bytes, prefix };
  }

  /** 4 bytes for IPv4, 16 bytes for IPv6, null when invalid. */
  static parseIp(text: string): number[] | null {
    if (!text) {
      return null;
    }
    return text.includes(":")
      ? SecurityValidators.parseIpv6(text)
      : SecurityValidators.parseIpv4(text);
  }

  private static parseIpv4(text: string): number[] | null {
    const octets = text.split(".");
    if (octets.length !== 4) {
      return null;
    }
    const bytes: number[] = [];
    for (const octet of octets) {
      if (!/^\d{1,3}$/.test(octet)) {
        return null;
      }
      const n = Number(octet);
      if (n > 255) {
        return null;
      }
      bytes.push(n);
    }
    return bytes;
  }

  private static parseIpv6(text: string): number[] | null {
    // Strip a zone id (fe80::1%eth0) — not meaningful for an allowlist.
    if (text.includes("%")) {
      return null;
    }
    const doubleColon = text.split("::");
    if (doubleColon.length > 2) {
      return null;
    }
    const parseGroups = (part: string): number[] | null => {
      if (part === "") {
        return [];
      }
      const groups = part.split(":");
      const result: number[] = [];
      for (let i = 0; i < groups.length; i++) {
        const group = groups[i];
        const isLast = i === groups.length - 1;
        if (isLast && group.includes(".")) {
          const v4 = SecurityValidators.parseIpv4(group);
          if (!v4) {
            return null;
          }
          result.push((v4[0] << 8) | v4[1], (v4[2] << 8) | v4[3]);
          continue;
        }
        if (!/^[0-9a-fA-F]{1,4}$/.test(group)) {
          return null;
        }
        result.push(parseInt(group, 16));
      }
      return result;
    };
    const head = parseGroups(doubleColon[0]);
    const tail = doubleColon.length === 2 ? parseGroups(doubleColon[1]) : [];
    if (!head || !tail) {
      return null;
    }
    let groups: number[];
    if (doubleColon.length === 2) {
      const missing = 8 - head.length - tail.length;
      if (missing < 1) {
        return null;
      }
      groups = [...head, ...new Array(missing).fill(0), ...tail];
    } else {
      groups = head;
    }
    if (groups.length !== 8) {
      return null;
    }
    const bytes: number[] = [];
    groups.forEach((g) => bytes.push((g >> 8) & 0xff, g & 0xff));
    return bytes;
  }

  /** True when `ip` falls inside `cidr` (same address family only). */
  static cidrContains(cidr: string, ip: string): boolean {
    const parsedBlock = SecurityValidators.parseCidr(cidr);
    const parsedAddress = SecurityValidators.parseIp(ip);
    if (!parsedBlock || !parsedAddress) {
      return false;
    }
    // Both the entry and the caller IP may be IPv4-mapped IPv6: compare them as IPv4.
    const block = SecurityValidators.unmapIpv4(parsedBlock.bytes, parsedBlock.prefix);
    const address = SecurityValidators.unmapIpv4(parsedAddress, parsedAddress.length * 8).bytes;
    if (address.length !== block.bytes.length) {
      return false;
    }
    let bits = block.prefix;
    for (let i = 0; i < address.length && bits > 0; i++) {
      const mask = bits >= 8 ? 0xff : (0xff << (8 - bits)) & 0xff;
      if ((address[i] & mask) !== (block.bytes[i] & mask)) {
        return false;
      }
      bits -= 8;
    }
    return true;
  }
}
